import { GitMerge } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import type { CandidateOwner } from './CandidateOwner'
import { MATCH_TIERS, type MatchTier, TIER_TONE } from './match-tier'
import {
  CENTER_RADIUS,
  LABEL_HEIGHT,
  NODE_RADIUS,
  STAR_MAX_CANDIDATES,
  STAR_SIZE,
  type StarLayoutInput,
  starLayout,
} from './star-layout'

/** Room for the pill drawn on a ring's top edge; the badge centres inside it. */
const PILL_WIDTH = 170
const PILL_HEIGHT = 22

/** The badge that marks another identity's face sits on its node's rim, at 45°. */
const OWNER_BADGE_RADIUS = 10
const OWNER_BADGE_OFFSET = NODE_RADIUS * Math.SQRT1_2
const OWNER_ICON_SIZE = 12

/** One candidate on the star. Thumbnails arrive as blob URLs from the parent. */
export interface StarCandidate extends StarLayoutInput {
  /** Blob URL of the face crop, or null while it loads. */
  imageUrl: string | null
  /** Already formatted similarity, e.g. `0.71`. */
  score: string
  /** Where the face came from, for the accessible name. */
  documentName: string
  /** The page to name, from 1: a PDF's face only (`facePageNumber`). */
  pageNumber?: number | null
  /** Raw quality flag names; the view that shows them translates them. */
  qualityFlags?: string[]
  /**
   * The identity this face is already confirmed to, when it is another one: the
   * candidate then proposes merging the two identities (ADR-0042).
   */
  assignedTo?: CandidateOwner | null
}

/** The node's accessible name: its tier, and whose face it already is. */
function nodeLabelKey(tier: MatchTier, assigned: boolean): string {
  if (assigned) return tier === 'suggested' ? 'assignedSuggestedCandidate' : 'assignedCandidate'
  return tier === 'suggested' ? 'suggestedCandidate' : 'candidate'
}

/**
 * Marks a node whose face another identity owns — merge, not confirm. Decoration
 * only: the node's own accessible name says whose face it is.
 */
function OwnerBadge({ x, y }: { x: number; y: number }) {
  const cx = x + OWNER_BADGE_OFFSET
  const cy = y - OWNER_BADGE_OFFSET
  return (
    <g data-owner-badge="">
      <circle
        cx={cx}
        cy={cy}
        r={OWNER_BADGE_RADIUS}
        className="fill-info stroke-card"
        strokeWidth={2}
      />
      <GitMerge
        x={cx - OWNER_ICON_SIZE / 2}
        y={cy - OWNER_ICON_SIZE / 2}
        size={OWNER_ICON_SIZE}
        strokeWidth={2.5}
        className="text-card"
      />
    </g>
  )
}

export interface StarViewProps {
  /** Blob URL of the identity's main photo. */
  centerImageUrl: string | null
  /** The identity's name, already resolved by the caller. */
  centerLabel: string
  /** Score-ordered pending candidates; only the first seven are drawn. */
  candidates: StarCandidate[]
  /** Open the document detail overlay on a candidate's face. */
  onSelect: (faceId: string) => void
  /** The candidate last opened in the overlay, highlighted on the star. */
  selectedFaceId?: string | null
  /** The candidate hovered or focused anywhere — here or in the details panel. */
  activeFaceId?: string | null
  /** Hovering or focusing a node reports it, so the panel can follow. */
  onActiveChange?: (faceId: string | null) => void
  className?: string
}

/**
 * The identity's strongest pending suggestions around its main photo (ADR-0040):
 * the suggested matches on the inner ring, every other suggestion on the outer
 * one, each ring named by the pill on its edge. Only a suggested match carries a
 * colour — node edge, ring and spoke — and every node shows its similarity, so
 * the number is never hidden behind a hover. A face already confirmed to another
 * identity wears a merge badge on its rim (ADR-0042): deciding it merges the two
 * identities rather than confirming the face.
 *
 * Pure SVG — around 30 nodes need no graph library, and it keeps every colour
 * on a token. Carries no ER assumptions, so PRD-0031 can reuse it.
 */
export function StarView({
  centerImageUrl,
  centerLabel,
  candidates,
  onSelect,
  selectedFaceId = null,
  activeFaceId = null,
  onActiveChange,
  className,
}: StarViewProps) {
  const { t } = useTranslation()
  const base = 'cases.investigate.entityResolution.photoMatching'
  const { center, nodes, rings } = starLayout(candidates, {
    size: STAR_SIZE,
    maxCandidates: STAR_MAX_CANDIDATES,
  })

  if (nodes.length === 0) {
    return <p className="px-1 py-4 text-sm text-muted-foreground">{t(`${base}.star.empty`)}</p>
  }

  return (
    <figure className={cn('m-0 flex min-w-0 flex-col items-center gap-3', className)}>
      <svg
        viewBox={`0 0 ${STAR_SIZE} ${STAR_SIZE}`}
        className="h-auto w-full max-w-[28rem] text-muted-foreground"
        role="group"
        aria-label={t(`${base}.star.title`)}
      >
        <defs>
          <clipPath id="face-star-center-clip">
            <circle cx={center} cy={center} r={CENTER_RADIUS} />
          </clipPath>
          {nodes.map(node => (
            <clipPath key={node.candidate.faceId} id={`face-star-clip-${node.candidate.faceId}`}>
              <circle cx={node.x} cy={node.y} r={NODE_RADIUS} />
            </clipPath>
          ))}
        </defs>

        {rings.map(ring => (
          <circle
            key={ring.tier}
            cx={center}
            cy={center}
            r={ring.radius}
            className={cn('fill-none', TIER_TONE[ring.tier].ring)}
            strokeDasharray="4 4"
          />
        ))}

        <g>
          <circle
            cx={center}
            cy={center}
            r={CENTER_RADIUS}
            className="fill-muted stroke-border"
            strokeWidth={2}
          />
          {centerImageUrl ? (
            <image
              href={centerImageUrl}
              x={center - CENTER_RADIUS}
              y={center - CENTER_RADIUS}
              width={CENTER_RADIUS * 2}
              height={CENTER_RADIUS * 2}
              preserveAspectRatio="xMidYMid slice"
              clipPath="url(#face-star-center-clip)"
            />
          ) : null}
          <title>{t(`${base}.star.center`, { name: centerLabel })}</title>
        </g>

        {nodes.map(node => {
          const { faceId, imageUrl, score, documentName, assignedTo } = node.candidate
          const selected = faceId === selectedFaceId
          const active = faceId === activeFaceId
          const tone = TIER_TONE[node.tier]
          return (
            // biome-ignore lint/a11y/useSemanticElements: SVG has no <button>.
            <g
              key={faceId}
              role="button"
              tabIndex={0}
              aria-label={t(`${base}.star.${nodeLabelKey(node.tier, Boolean(assignedTo))}`, {
                document: documentName,
                score,
                name: assignedTo?.displayName,
              })}
              data-face-id={faceId}
              data-tier={node.tier}
              data-assigned-to={assignedTo?.personId}
              data-active={active || selected ? 'true' : undefined}
              className="group cursor-pointer outline-none"
              onClick={() => onSelect(faceId)}
              onMouseEnter={() => onActiveChange?.(faceId)}
              onMouseLeave={() => onActiveChange?.(null)}
              onFocus={() => onActiveChange?.(faceId)}
              onBlur={() => onActiveChange?.(null)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onSelect(faceId)
                }
              }}
            >
              <line
                x1={center}
                y1={center}
                x2={node.x}
                y2={node.y}
                className={tone.edge}
                strokeWidth={active || selected ? 3 : 1.5}
              />
              {/* The halo marks the hovered, focused or open candidate — it is
                  the same highlight the details panel row raises. */}
              {active || selected ? (
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={NODE_RADIUS + 5}
                  className={cn('fill-none', tone.node, 'opacity-40')}
                  strokeWidth={2}
                />
              ) : null}
              <circle
                cx={node.x}
                cy={node.y}
                r={NODE_RADIUS}
                className={cn('fill-muted', tone.node)}
                strokeWidth={active || selected ? 4 : 2.5}
              />
              {imageUrl ? (
                <image
                  href={imageUrl}
                  x={node.x - NODE_RADIUS}
                  y={node.y - NODE_RADIUS}
                  width={NODE_RADIUS * 2}
                  height={NODE_RADIUS * 2}
                  preserveAspectRatio="xMidYMid slice"
                  clipPath={`url(#face-star-clip-${faceId})`}
                />
              ) : null}
              {assignedTo ? <OwnerBadge x={node.x} y={node.y} /> : null}
              {/* The similarity is always shown; hover and focus only weigh it. */}
              <text
                x={node.x}
                y={node.y + NODE_RADIUS + LABEL_HEIGHT - 2}
                textAnchor="middle"
                data-similarity={score}
                className={cn(
                  'fill-current text-[11px] tabular-nums group-hover:font-semibold group-focus-visible:font-semibold',
                  node.tier === 'suggested' ? 'text-foreground' : 'text-muted-foreground',
                  (active || selected) && 'font-semibold'
                )}
              >
                {score}
              </text>
            </g>
          )
        })}

        {/* The pills ride the rings' top edges, above every node, so a ring is
            named where it is drawn. */}
        {rings.map(ring => (
          <foreignObject
            key={ring.tier}
            x={ring.labelX - PILL_WIDTH / 2}
            y={ring.labelY - PILL_HEIGHT / 2}
            width={PILL_WIDTH}
            height={PILL_HEIGHT}
          >
            <div className="flex h-full w-full items-center justify-center">
              <Badge variant={TIER_TONE[ring.tier].badge} data-tier={ring.tier}>
                {t(`${base}.tiers.${ring.tier}`)}
              </Badge>
            </div>
          </foreignObject>
        ))}
      </svg>

      <figcaption className="flex w-full flex-col items-center gap-2">
        <ul className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {MATCH_TIERS.map(tier => (
            <li key={tier} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={cn('size-2 shrink-0 rounded-full', TIER_TONE[tier].dot)} />
              <span className="text-foreground">{t(`${base}.tiers.${tier}`)}</span>
            </li>
          ))}
          {/* The badge explains itself only where one is drawn. */}
          {nodes.some(node => node.candidate.assignedTo) ? (
            <li
              data-legend="assigned"
              className="flex items-center gap-1.5 text-xs text-muted-foreground"
            >
              <GitMerge className="size-3 shrink-0 text-info" aria-hidden />
              <span className="text-foreground">{t(`${base}.assigned.legend`)}</span>
            </li>
          ) : null}
        </ul>
        <span className="text-center text-2xs text-muted-foreground">{t(`${base}.star.hint`)}</span>
      </figcaption>
    </figure>
  )
}
